import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { ButtonZoom } from "../../v2/utils/ButtonZoom";
import { useContext, useEffect, useState } from "react";
import { CourseStructureContext } from "../../../context/CourseStructureContext";
import { CourseStructureCtx, UniversalModalCtx } from "../../../interfaces";
import { useHistory, useLocation } from "react-router-dom";
import { Course, Module } from "../../utils/CourseTree";
import { ModuleContent } from "./ModuleContent";
import { UniversalModalContext } from "../../../context/UniversalModalContext";
import { HandleDataLayer } from "../../../Helpers";
import axios from "../../../services/axios";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { Role } from "../../../enums";
import { UserCtx } from "../../../interfaces";
import { UserContext } from "../../../context/UserContext";

interface SingleModuleCardProps {
  module: Module;
  course: Course;
  userCtx: UserCtx;
}

const SingleModuleCard = (props: SingleModuleCardProps) => {
  const history = useHistory();
  const { module, course } = props;
  const courseMetadata = course.getMetadata();
  const universalModalCtx = useContext(UniversalModalContext) as UniversalModalCtx;
  const title =
    module.metadata && "AZ_state_standard_code" in module.metadata
      ? module.metadata.AZ_state_standard_code
      : module.id.split(".").splice(0, 4).join(".");
  const currentModuleName = module.getFormattedModuleName();

  return (
    <Box
      role="button"
      tabIndex={0}
      sx={{
        background: "#FFFFFF 0% 0% no-repeat padding-box",
        border: "2px solid #DEDEDE",
        borderRadius: "16px",
        padding: "32px",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: "120px",
        "&:hover": {
          boxShadow: "0px 8px 12px 0px #00000029",
          cursor: "pointer",
        },
        "&:focus, &:focus-visible": {
          boxShadow: "0px 8px 12px 0px #00000029",
        },
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.currentTarget.click();
        }
      }}
      onClick={() => {
        HandleDataLayer(
          "link",
          "click",
          "internal link",
          "main content",
          currentModuleName,
          "view content",
          "onclick",
          `${course.name + " ^ module activities"}`
        );

        const targetElement = document.getElementsByTagName("header");
        if (targetElement.length > 0) {
          targetElement[0].scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "start",
          });
        }

        history.push({
          search: `?module=${module.id}&courseId=${course.id}&content=1`,
        });
      }}>
      <Stack direction="column" spacing={"16px"} sx={{ flex: 1 }}>
        <Typography
          sx={{
            font: "normal normal bold 24px/54px proxima-soft",
            lineHeight: "normal",
            color: "#191919",
            "&:hover": {
              textDecoration: "underline",
            },
          }}>
          {currentModuleName}
        </Typography>
        {!courseMetadata?.disable_AZ_math_standard && (
          <Typography
            sx={{
              font: "normal normal normal 16px/24px Roboto",
              color: "#747474",
              cursor: "pointer",
            }}>
            AZ Math:{" "}
            {
              <Tooltip title={currentModuleName} placement={"bottom"} arrow>
                <Typography
                  onClick={() => {
                    HandleDataLayer(
                      "modal",
                      "open",
                      "click",
                      "main content",
                      currentModuleName,
                      `${title}`,
                      "onclick",
                      `${course.name + " ^ module activities"}`
                    );
                    if (module.metadata && "AZ_state_standard" in module.metadata) {
                      universalModalCtx.setSimpleModalContent({
                        title: `AZ Math: ${title}`,
                        subtitle: currentModuleName,
                        body:
                          module.metadata.AZ_state_standard?.split("\n")
                            .map((item, index) => {
                              return `${item}`;
                            })
                            .join("\n") || "",
                      });
                      universalModalCtx.setSimpleContentModalOpen(true);
                    }
                  }}
                  component={"span"}
                  sx={{
                    font: "normal normal normal 16px/24px Roboto",
                    textDecoration: "underline",
                    color: "#747474",
                  }}>
                  {title}
                </Typography>
              </Tooltip>
            }
          </Typography>
        )}
      </Stack>

      <Tooltip
        placement="bottom"
        arrow
        title={`View module as ${props.userCtx.dbUser.role}`}
        aria-hidden={true}>
        <Box
          sx={{
            width: 64,
            height: 64,
            border: "2px solid #D0D0D0",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            "&:hover": {
              background: "#E2F7FF",
            },
            "&:active": {
              background: "#C7EBFF",
            },
          }}>
          <i
            className="fa-regular fa-play"
            style={{
              color: "#0092E0",
              fontSize: 28,
            }}
          />
        </Box>
      </Tooltip>
    </Box>
  );
};

export const ModuleActivity = () => {
  const location = useLocation();
  const history = useHistory();
  const userCtx = useContext(UserContext) as UserCtx;
  const _showModuleContent = new URLSearchParams(location.search).get("content");
  const _selectedModule = new URLSearchParams(location.search).get("module");
  const courseStructure = useContext(CourseStructureContext) as CourseStructureCtx;
  const course = courseStructure.courseTreeStructure?.getCurrentCourse();
  const allModules = courseStructure.courseTreeStructure?.getAllModulesInCourse();
  const [showModuleContent, setShowModuleContent] = useState(false);
  useEffect(() => {
    if (_showModuleContent && Number(_showModuleContent) === 1) {
      setShowModuleContent(true);
    } else {
      setShowModuleContent(false);
    }
  }, [_showModuleContent]);
  return (
    <>
      {!showModuleContent && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexWrap: "nowrap",
          }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
            <Typography
              sx={{ color: "#000000", font: "normal normal bold 28px/54px proxima-soft" }}
              variant="h2"
              component="span">
              All modules
            </Typography>
            {(userCtx.dbUser?.role === Role.teacher ||
              userCtx.dbUser.role === Role.admin ||
              userCtx.dbUser.role === Role.super) && (
              <ButtonZoom
                size="large"
                variant="contained"
                aria-label={"View this course as a student, button, not pressed."}
                sx={{
                  minWidth: "242px",
                  "&:active": {
                    border: "2px solid #191919",
                  },
                }}
                onClick={async () => {
                  try {
                    const fullUrl = window.location.href;
                    MacsAuthenticator.authHook.setExitStudentViewPath(fullUrl);

                    const res = await axios.post(`/user/start-student-view`, null, {
                      params: {
                        launchedCourseId: course?.id,
                        ltik: MacsAuthenticator.authHook.ltik,
                      },
                    });

                    if (res.status === 200) {
                      const newClassId = res.data?.newClassId;
                      const launchedAssignmentId = res.data?.launchedAssignmentId;
                      if (launchedAssignmentId)
                        MacsAuthenticator.authHook.setLaunchedAssignmentId(
                          launchedAssignmentId
                        );
                      MacsAuthenticator.authHook.setIsStudentView("true");
                      //Set the role on the frontend temporarily
                      userCtx.dbUser.role = Role.student;
                      history.push({
                        pathname: "/learning-overview",
                        search: new URLSearchParams({
                          classId: newClassId,
                        }).toString(),
                      });
                      window.location.reload();
                    }
                  } catch (e: any) {
                    console.error("Failed to start student view", e);
                    throw e;
                  }
                }}>
                <Typography variant="h2" component="span" sx={{ fontSize: "1.3125rem" }}>
                  <i
                    className="fa-regular fa-graduation-cap"
                    style={{ marginRight: "16px" }}></i>{" "}
                  View as student
                </Typography>
              </ButtonZoom>
            )}
          </Box>
          <Box
            sx={{
              marginTop: "32px",
              display: "flex",
              flexDirection: "column",
              flexWrap: "nowrap",
              gap: "16px",
            }}>
            {course &&
              allModules?.map((item, index) => {
                return (
                  <SingleModuleCard
                    key={index}
                    module={item}
                    course={course}
                    userCtx={userCtx}
                  />
                );
              })}
          </Box>
        </Box>
      )}
      {showModuleContent && _selectedModule && (
        <Box sx={{ display: "flex", flexDirection: "column", flexWrap: "nowrap" }}>
          <ModuleContent moduleId={_selectedModule} />
        </Box>
      )}
    </>
  );
};
